import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowForward,
  AssignmentInd,
  BarChart,
  DirectionsCar,
  InfoOutlined,
  Inventory,
  LocalShipping,
  Person,
  Schedule,
  Speed,
} from "@mui/icons-material";
import { ordersEventsDriverAvailability } from "../../../../api/order";

const DriverAvailabilityAnalysis = ({ startDate, endDate }) => {
  const theme = useTheme();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!startDate || !endDate) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });

        const response = await ordersEventsDriverAvailability(params);
        const data = await response.data;
        setAnalysisData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [startDate, endDate]);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getPerformanceColor = (time, maxTime) => {
    const ratio = time / maxTime;
    if (ratio <= 0.33) return theme.palette.success.main;
    if (ratio <= 0.66) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Process explanation component
  const renderProcessExplanation = () => (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.info.main, 0.03),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
            <InfoOutlined />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="info.main">
              O que esta análise mede?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tempo de despacho: desde quando o pedido fica pronto até ser
              atribuído ao motorista
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.05),
            border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            flexWrap="wrap"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  width: 32,
                  height: 32,
                }}
              >
                <Inventory sx={{ fontSize: 16 }} />
              </Avatar>
              <Stack>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="warning.main"
                >
                  PKD - EMBALADO
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pedido pronto
                </Typography>
              </Stack>
            </Stack>

            <ArrowForward
              sx={{ color: theme.palette.primary.main, fontSize: 24 }}
            />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  backgroundColor: theme.palette.error.main,
                  width: 32,
                  height: 32,
                }}
              >
                <LocalShipping sx={{ fontSize: 16 }} />
              </Avatar>
              <Stack>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="error.main"
                >
                  DSP - DESPACHADO
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Atribuído ao motorista
                </Typography>
              </Stack>
            </Stack>

            <Stack alignItems="center" spacing={0.5}>
              <Schedule
                sx={{ color: theme.palette.primary.main, fontSize: 20 }}
              />
              <Typography variant="caption" fontWeight={600} color="primary">
                Tempo de Despacho
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );

  const renderDriverMetricCard = (driver, index, maxTime) => {
    const performanceColor = getPerformanceColor(
      driver.averageTimeToNext,
      maxTime
    );

    return (
      <Paper
        key={`driver-${driver.driverId || "unassigned"}-${index}`}
        elevation={2}
        sx={{
          p: 2.5,
          borderRadius: 3,
          backgroundColor: alpha(performanceColor, 0.03),
          border: `1px solid ${alpha(performanceColor, 0.2)}`,
          transition: "all 0.3s ease",
          "&:hover": {
            elevation: 4,
            transform: "translateY(-2px)",
          },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                backgroundColor: performanceColor,
                width: 40,
                height: 40,
              }}
            >
              {driver.driverId ? (
                <Person sx={{ fontSize: 20 }} />
              ) : (
                <AssignmentInd sx={{ fontSize: 20 }} />
              )}
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                {driver.driverId
                  ? driver.driverName || `Motorista ${driver.driverId}`
                  : "Não Atribuído"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {driver.totalEvents} pedidos embalados
                {driver.driverId && ` • ID: ${driver.driverId}`}
              </Typography>
            </Box>
            <Tooltip
              title={
                driver.driverId
                  ? `Tempo médio para ${
                      driver.driverName || `Motorista ${driver.driverId}`
                    } receber pedidos prontos (PKD→DSP)`
                  : "Tempo médio para pedidos sem motorista atribuído serem despachados"
              }
              arrow
            >
              <IconButton size="small">
                <InfoOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="caption" color="text.secondary">
                Tempo Médio PKD→DSP
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={performanceColor}
              >
                {formatTime(driver.averageTimeToNext)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(driver.averageTimeToNext / maxTime) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(performanceColor, 0.1),
                "& .MuiLinearProgress-bar": {
                  backgroundColor: performanceColor,
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          <Stack direction="row" justifyContent="space-between">
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                Mais Rápido
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatTime(driver.minTime)}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                Mais Lento
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatTime(driver.maxTime)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderHourlyPattern = (pattern) => {
    if (!pattern.averageTimeToNext) {
      return;
    }

    const maxEfficiency = Math.max(
      ...analysisData.hourlyDriverPatterns.map((p) => p.driverEfficiency)
    );
    const efficiency = pattern.driverEfficiency;
    const efficiencyRatio =
      maxEfficiency > 0 ? (efficiency / maxEfficiency) * 100 : 0;

    const hasData = pattern.totalEvents > 0;

    return (
      <Tooltip
        key={`hour-${pattern.hour}`}
        title={
          hasData
            ? `${pattern.hour}:00 - ${
                pattern.totalEvents
              } pedidos despachados em média ${formatTime(
                pattern.averageTimeToNext
              )} (PKD→DSP) com ${
                pattern.driversAvailable
              } motoristas disponíveis`
            : `${pattern.hour}:00 - Nenhum pedido despachado neste horário`
        }
        arrow
      >
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: hasData
              ? alpha(theme.palette.primary.main, 0.02)
              : alpha(theme.palette.grey[300], 0.02),
            border: `1px solid ${
              hasData
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.grey[300], 0.2)
            }`,
            minHeight: 140,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: hasData
                ? alpha(theme.palette.primary.main, 0.05)
                : alpha(theme.palette.grey[300], 0.05),
            },
          }}
        >
          <Stack spacing={1} alignItems="center">
            <Typography
              variant="caption"
              fontWeight={600}
              color={hasData ? "primary" : "text.secondary"}
            >
              {pattern.hour}:00
            </Typography>

            <Box
              sx={{
                width: 8,
                height: 60,
                backgroundColor: hasData
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.grey[300], 0.1),
                borderRadius: 1,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {hasData && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    height: `${Math.max(efficiencyRatio, 5)}%`,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 1,
                    transition: "height 0.3s ease",
                  }}
                />
              )}
            </Box>

            <Stack spacing={0.5} alignItems="center">
              <Chip
                label={pattern.driversAvailable}
                size="small"
                color={hasData ? "primary" : "default"}
                variant={hasData ? "filled" : "outlined"}
                sx={{
                  fontSize: "0.65rem",
                  height: 16,
                  opacity: hasData ? 1 : 0.5,
                }}
              />
              <Typography
                variant="caption"
                color={hasData ? "text.primary" : "text.secondary"}
                textAlign="center"
                fontWeight={hasData ? 600 : 400}
              >
                {hasData ? formatTime(pattern.averageTimeToNext) : "Sem dados"}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Tooltip>
    );
  };

  const renderLoadingState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<DirectionsCar />}
        title="Análise de Disponibilidade de Motoristas"
        sx={{ backgroundColor: theme.palette.primary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress size={40} />
        </Box>
      </CardContent>
    </Card>
  );

  const renderErrorState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<DirectionsCar />}
        title="Análise de Disponibilidade de Motoristas"
        sx={{ backgroundColor: theme.palette.primary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Erro ao carregar dados de análise: {error}
        </Alert>
      </CardContent>
    </Card>
  );

  if (loading) return renderLoadingState();
  if (error) return renderErrorState();
  if (!analysisData) return null;

  const maxDriverTime = Math.max(
    ...analysisData.driverAvailabilityMetrics.driverMetrics.map(
      (d) => d.averageTimeToNext
    )
  );

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
            <DirectionsCar />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={700}>
            Análise de Disponibilidade de Motoristas
          </Typography>
        }
        subheader={
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}
          >
            Tempo de despacho: do pedido pronto (PKD) até atribuição ao
            motorista (DSP)
          </Typography>
        }
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          "& .MuiCardHeader-avatar": { mr: 2 },
        }}
      />

      <CardContent sx={{ p: 3 }}>
        {/* Process Explanation */}
        {renderProcessExplanation()}

        {/* Summary Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ backgroundColor: theme.palette.success.main }}>
                  <Speed />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Motoristas Ativos
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="success.main"
                  >
                    {
                      analysisData.driverAvailabilityMetrics
                        .totalDriversAvailable
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receberam pedidos PKD
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>
                  <AssignmentInd />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Despachos sem Motorista
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="warning.main"
                  >
                    {
                      analysisData.driverAvailabilityMetrics
                        .totalUnassignedOrders
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PKD→DSP sem atribuição inicial
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
                  <BarChart />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Despachos Analisados
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="info.main">
                    {analysisData.totalPkdEvents}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Transições PKD→DSP
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Hourly Patterns Section */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Tempo de Despacho por Hora do Dia
            </Typography>
            <Tooltip
              title="Mostra o tempo médio PKD→DSP em cada hora. Barras mais altas indicam despachos mais lentos. Passe o mouse sobre cada hora para mais detalhes."
              arrow
            >
              <IconButton size="small">
                <InfoOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Tempo médio do pedido pronto (PKD/Embalado) até atribuição ao
            motorista (DSP/Despachado)
          </Typography>

          <Box
            sx={{
              overflowX: "auto",
              pb: 2,
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.grey[300], 0.3),
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
                borderRadius: 4,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.7),
                },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ minWidth: "max-content", py: 1 }}
            >
              {analysisData.hourlyDriverPatterns.map(renderHourlyPattern)}
            </Stack>
          </Box>

          <Stack
            direction="row"
            justifyContent="center"
            spacing={4}
            mt={2}
            flexWrap="wrap"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 1,
                  backgroundColor: theme.palette.primary.main,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Tempo PKD→DSP (altura da barra)
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label="N"
                sx={{ fontSize: "0.65rem", height: 16 }}
              />
              <Typography variant="caption" color="text.secondary">
                Motoristas Disponíveis
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Schedule sx={{ fontSize: 14, color: theme.palette.info.main }} />
              <Typography variant="caption" color="text.secondary">
                Passe o mouse para detalhes
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DriverAvailabilityAnalysis;
